import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import apiClient from '../api/client';
import { useTheme } from '../contexts/ThemeContext';
import { Upload, Download, Eye, Trash2, File, FileText, Filter, Search } from 'lucide-react';

const Documents = () => {
  const { isDarkMode } = useTheme();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(false);
  const [filterType, setFilterType] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [uploadForm, setUploadForm] = useState({
    document_type: 'general',
    description: ''
  });

  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    try {
      const params = filterType !== 'all' ? `?document_type=${filterType}` : '';
      const response = await apiClient.get(`/api/documents${params}`);
      setDocuments(response.data);
    } catch (error) {
      console.error('Error fetching documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    
    if (!selectedFile) {
      alert('Please select a file');
      return;
    }

    setUploadProgress(true);
    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('document_type', uploadForm.document_type);

    try {
      await apiClient.post('/api/documents/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      alert('Document uploaded successfully!');
      fetchDocuments();
      setShowUploadModal(false);
      setSelectedFile(null);
      setUploadForm({ document_type: 'general', description: '' });
    } catch (error) {
      console.error('Error uploading document:', error);
      alert('Error uploading document');
    } finally {
      setUploadProgress(false);
    }
  };

  const handleDownload = async (docId, filename) => {
    try {
      const response = await apiClient.get(`/api/documents/${docId}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error downloading document:', error);
      alert('Error downloading document');
    }
  };

  const handleDelete = async (docId, filename) => {
    if (!window.confirm(`Are you sure you want to delete ${filename}?`)) {
      return;
    }

    try {
      await apiClient.delete(`/api/documents/${docId}`);
      alert('Document deleted successfully');
      fetchDocuments();
    } catch (error) {
      console.error('Error deleting document:', error);
      alert('Error deleting document');
    }
  };

  const getDocumentIcon = (type) => {
    switch(type) {
      case 'attendance_upload': return '📊';
      case 'salary_upload': return '💰';
      case 'employee_upload': return '👥';
      case 'kpi_upload': return '📈';
      case 'incentive_upload': return '🎯';
      default: return '📄';
    }
  };

  const getDocumentTypeLabel = (type) => {
    const labels = {
      'attendance_upload': 'Attendance Upload',
      'salary_upload': 'Salary Statement',
      'employee_upload': 'Employee Directory',
      'kpi_upload': 'KPI Goals',
      'incentive_upload': 'Incentive Scheme',
      'general': 'General Document',
      'other': 'Other'
    };
    return labels[type] || type;
  };

  const filteredDocuments = documents.filter(doc =>
    doc.filename?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    doc.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <Layout>
        <div className='flex items-center justify-center h-full'>
          <div className='loading-spinner'></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className='page-header flex items-center justify-between'>
        <div>
          <h1 className={`text-3xl font-bold ${isDarkMode ? 'text-[hsl(210_15%_92%)]' : 'text-gray-900'}`}>Document Management</h1>
          <p className={`mt-1 ${isDarkMode ? 'text-[hsl(215_10%_70%)]' : 'text-gray-600'}`}>Manage and access all uploaded documents - {documents.length} files</p>
        </div>
        <button
          onClick={() => setShowUploadModal(true)}
          data-testid='upload-document-button'
          className='flex items-center gap-2 bg-[#00B8B8] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#0ED2D2] transition-all'
        >
          <Upload size={20} />
          Upload Document
        </button>
      </div>

      {/* Filters and Search */}
      <div className={`rounded-xl border p-4 mb-6 ${isDarkMode ? 'bg-[hsl(220_6%_8%)] border-[hsl(215_10%_22%)]' : 'bg-white border-gray-200'}`}>
        <div className='flex items-center gap-4 flex-wrap'>
          <div className='flex items-center gap-2'>
            <Filter size={18} className={isDarkMode ? 'text-[hsl(215_10%_70%)]' : 'text-gray-500'} />
            <select
              value={filterType}
              onChange={(e) => {
                setFilterType(e.target.value);
                fetchDocuments();
              }}
              className={`px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#00B8B8] ${isDarkMode ? 'bg-[hsl(220_6%_12%)] border-[hsl(215_10%_22%)] text-[hsl(210_15%_92%)]' : 'bg-white border-gray-200 text-gray-900'}`}
            >
              <option value='all'>All Documents</option>
              <option value='attendance_upload'>Attendance Uploads</option>
              <option value='salary_upload'>Salary Statements</option>
              <option value='employee_upload'>Employee Directory</option>
              <option value='kpi_upload'>KPI Files</option>
              <option value='incentive_upload'>Incentive Files</option>
              <option value='general'>General</option>
            </select>
          </div>
          
          <div className='flex-1 relative'>
            <Search className='absolute left-3 top-1/2 transform -translate-y-1/2 text-[#737373]' size={18} />
            <input
              type='text'
              placeholder='Search documents...'
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className='w-full pl-10 pr-4 py-2 border border-[#E5E5E5] rounded-lg focus:ring-2 focus:ring-[#7EC845]'
            />
          </div>
        </div>
      </div>

      {/* Documents Grid */}
      <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'>
        {filteredDocuments.length === 0 ? (
          <div className='col-span-full text-center py-20 text-[#737373]'>
            <File size={64} className='mx-auto mb-4 opacity-50' />
            <p className='text-lg font-medium'>No documents found</p>
            <p className='text-sm mt-2'>Upload documents or adjust filters</p>
          </div>
        ) : (
          filteredDocuments.map((doc) => (
            <div key={doc.id} className='bg-white rounded-xl border border-[#E5E5E5] p-6 hover:shadow-lg transition-all'>
              <div className='flex items-start justify-between mb-4'>
                <div className='flex items-center gap-3'>
                  <span className='text-4xl'>{getDocumentIcon(doc.document_type)}</span>
                  <div>
                    <h3 className='font-semibold text-[#262626] break-words'>{doc.filename}</h3>
                    <span className='text-xs text-[#737373] bg-[#F5F5F5] px-2 py-1 rounded-full mt-1 inline-block'>
                      {getDocumentTypeLabel(doc.document_type)}
                    </span>
                  </div>
                </div>
              </div>
              
              <div className='space-y-2 mb-4'>
                <p className='text-sm text-[#737373]'>
                  <strong>Size:</strong> {(doc.size / 1024).toFixed(2)} KB
                </p>
                <p className='text-sm text-[#737373]'>
                  <strong>Uploaded:</strong> {new Date(doc.uploaded_at).toLocaleDateString()}
                </p>
                {doc.description && (
                  <p className='text-sm text-[#737373]'>
                    <strong>Description:</strong> {doc.description}
                  </p>
                )}
              </div>

              <div className='flex gap-2'>
                <button
                  onClick={() => handleDownload(doc.id, doc.filename)}
                  data-testid={`download-doc-${doc.id}`}
                  className='flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-[#7EC845] text-white rounded-lg hover:bg-[#6EB835] text-sm'
                >
                  <Download size={16} />
                  Download
                </button>
                <button
                  onClick={() => handleDelete(doc.id, doc.filename)}
                  data-testid={`delete-doc-${doc.id}`}
                  className='px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600'
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <div className='fixed inset-0 bg-black/50 flex items-center justify-center z-50' onClick={() => setShowUploadModal(false)}>
          <div className='bg-white rounded-xl p-6 max-w-md w-full m-4' onClick={(e) => e.stopPropagation()}>
            <h2 className='text-2xl font-bold text-[#262626] mb-6'>Upload Document</h2>
            
            <form onSubmit={handleUpload} className='space-y-4'>
              <div>
                <label className='block text-sm font-medium text-[#262626] mb-2'>Document Type*</label>
                <select
                  required
                  value={uploadForm.document_type}
                  onChange={(e) => setUploadForm({...uploadForm, document_type: e.target.value})}
                  className='w-full px-4 py-2 border border-[#E5E5E5] rounded-lg focus:ring-2 focus:ring-[#7EC845]'
                >
                  <option value='general'>General Document</option>
                  <option value='policy'>Policy Document</option>
                  <option value='contract'>Contract/Agreement</option>
                  <option value='certificate'>Certificate</option>
                  <option value='other'>Other</option>
                </select>
              </div>

              <div>
                <label className='block text-sm font-medium text-[#262626] mb-2'>Description</label>
                <textarea
                  value={uploadForm.description}
                  onChange={(e) => setUploadForm({...uploadForm, description: e.target.value})}
                  rows='2'
                  className='w-full px-4 py-2 border border-[#E5E5E5] rounded-lg focus:ring-2 focus:ring-[#7EC845]'
                  placeholder='Brief description of the document'
                ></textarea>
              </div>

              <div>
                <label className='block text-sm font-medium text-[#262626] mb-2'>Select File*</label>
                <input
                  type='file'
                  required
                  onChange={handleFileSelect}
                  data-testid='document-file-input'
                  className='w-full px-4 py-2 border border-[#E5E5E5] rounded-lg'
                />
                {selectedFile && (
                  <div className='mt-3 p-3 bg-green-50 border border-green-200 rounded-lg'>
                    <p className='text-sm text-green-800'>
                      <strong>Selected:</strong> {selectedFile.name} ({(selectedFile.size / 1024).toFixed(2)} KB)
                    </p>
                  </div>
                )}
              </div>

              <div className='flex gap-4 justify-end mt-6'>
                <button
                  type='button'
                  onClick={() => {
                    setShowUploadModal(false);
                    setSelectedFile(null);
                  }}
                  disabled={uploadProgress}
                  className='px-6 py-2 border border-[#E5E5E5] rounded-lg text-[#262626] hover:bg-[#F5F5F5] disabled:opacity-50'
                >
                  Cancel
                </button>
                <button
                  type='submit'
                  disabled={!selectedFile || uploadProgress}
                  data-testid='submit-document-upload'
                  className='px-6 py-2 bg-[#7EC845] text-white rounded-lg hover:bg-[#6EB835] disabled:opacity-50'
                >
                  {uploadProgress ? 'Uploading...' : 'Upload'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default Documents;